// AI Generated Test
import { Component, Input, OnInit } from '@angular/core';
import { TranslateService } from '@client/app/services/translate/translate.service';
import { UptimeRepoService } from '@client/app/services/api/uptime-repo/uptime-repo.service';
import { DateTimeService } from '@client/app/services/date-time/date-time.service';
import { SessionService } from '@monsido/core/session/session.service';
import { PipeService } from '@client/app/filters/pipe-transformation.service';
import { contextTokens } from '@monsido/ng2/services/request-auxiliary/request-auxiliary.service';
import { safeClone } from '@client/app/shared/utils/object-utils';

@Component({
    selector: 'domain-dashboard-heartbeat',
    standalone: false,
    template: `
        <div class="card panel-monsido-card max-height d-flex flex-column">
            <a class="p-0" [uiSref]="'base.customer.domain.uptimes.index'" [attr.aria-label]="'Uptime Monitoring' | translate">
                <mon-panel-header [pHeader]="'Uptime Monitoring' | translate"
                                  eClass="text-link">
                    <div class="text-right">
                        <div class="btn btn-link">
                            <span class="pl-1 fas fa-chevron-right" aria-hidden="true"></span>
                        </div>
                    </div>
                </mon-panel-header>
            </a>
            <div class="card-body flex-grow-1">
                <mon-spinner [loading]="loading">
                    <div class="row my-3">
                        <div class="offset-4 col-18">
                            <mon-chart-doughnut
                                    [labels]="doughnutLabels"
                                    [data]="doughnutData"
                                    [options]="doughnutOptions"
                                    [colors]="doughnutColors">
                            </mon-chart-doughnut>
                        </div>
                        <div class="col-26">
                            <div class="row">
                                <div class="col-48 text-grey" translate>
                                    Checkpoint:
                                </div>
                                <div class="col-48">
                                    <div class="text-overflow-container">
                                        <div class="text-overflow-item">
                                            <a *ngIf="uptime !== null"
                                               [uiSref]="'base.customer.domain.uptimes.show'"
                                               [uiParams]="{uptimeId: uptime.id}"
                                               class="cursor-pointer"
                                               [attr.aria-label]="'Go to uptime' | translate">
                                                {{uptime.url}}
                                            </a>
                                        </div>
                                    </div>
                                    <span *ngIf="uptime === null || uptime.url === null" class="text-grey" translate>
                                        Couldn't find any domains
                                    </span>
                                </div>

                                <div class="col-48 mt-2 text-grey" translate>
                                    Current status
                                </div>
                                <div class="col-48">
                                    <span class="fas fa-circle"
                                          [ngClass]="{'text-danger': domainStatus === 'DOWN', 'text-success': domainStatus === 'OK', 'text-warning' : domainStatus === 'INACTIVE'}">
                                    </span>
                                    {{domainStatus | translate}}
                                </div>

                                <div class="col-48 mt-2 text-grey" translate>
                                    Last downtime:
                                </div>
                                <div class="col-48">
                                    <span *ngIf="lastDowntime !== null">
                                        {{lastDowntime.start_at | monSetMyTimezone | userDate:'fullDate'}}
                                    </span>
                                    <span *ngIf="lastDowntime === null" class="text-grey" translate>
                                        Never
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </mon-spinner>
            </div>
        </div>
    `,
})
export class DomainDashboardHeartbeatComponent implements OnInit {
    @Input() historyData: any;

    loading: boolean = true;
    downTimeList: any[] = [];
    domainStatus: any = false;
    lastDowntime: any = null;
    uptimePercentage: number = 0;
    successRate: number = 0;
    uptime: any = null;
    doughnutColors: string[] = ['#4dc0bd', '#dcdcdc'];
    doughnutLabels: string[] = [];
    doughnutData: number[] = [];
    doughnutOptions: any = {};

    constructor (
        private pipeService: PipeService,
        private translateService: TranslateService,
        private uptimeRepoService: UptimeRepoService,
        private dateTimeService: DateTimeService,
        private sessionService: SessionService,
    ) {}

    ngOnInit (): void {
        this.activate();
    }

    private activate (): void {
        this.loading = true;
        this.downTimeList = [];
        this.domainStatus = false;
        this.lastDowntime = null;
        this.uptimePercentage = 0;
        this.successRate = 0;
        this.uptime = null;
        this.doughnutColors = ['#4dc0bd', '#dcdcdc'];
        this.doughnutLabels = [this.translateService.getString('Uptime'), this.translateService.getString('Downtime')];
        this.getUptimes();
    }

    private async getUptimes (): Promise<void> {
        const domainId = this.sessionService.getDomainId();
        const uptimes = await this.uptimeRepoService.getAll({}, domainId);
        let uptime: any;

        if (!Array.isArray(uptimes)) {
            this.loading = false;
            this.setupDoughnutChart();
            return;
        }

        uptime = uptimes.find((currentUptime: any) => {
            return currentUptime.type === 'primary';
        });

        if (!uptime && uptimes.length > 0) {
            uptime = uptimes[0];
        }

        if (uptime) {
            this.uptime = uptime;
            this.showUptimeData(uptime, domainId);
        } else {
            this.loading = false;
            this.setupDoughnutChart();
        }
    }

    private showUptimeData (uptime: any, domainId: any): void {
        const params = {
            start_date: this.dateTimeService.format(
                this.dateTimeService.startOf(
                    this.dateTimeService.substractDuration(undefined, 30, 'days'),
                    'day',
                ),
            ),
            end_date: this.dateTimeService.format(
                this.dateTimeService.endOf(
                    undefined,
                    'day',
                ),
            ),
            domain_id: domainId,
        };
        const promises: Promise<any>[] = [];
        promises.push(this.uptimeRepoService.getReport(uptime.id, params, contextTokens.NO_GLOBAL));
        promises.push(this.uptimeRepoService.getDowntimes(uptime.id, params));

        Promise.all(promises)
            .then((values: any[]) => {
                // If no pings have ever been made, the status is set to INACTIVE instead of DOWN
                if (values[0].data.length > 0) {
                    this.domainStatus = values[0].last_result ? 'OK' : 'DOWN';
                } else {
                    this.domainStatus = 'INACTIVE';
                }
                this.uptimePercentage = this.pipeService.get('monNumber')(parseFloat(values[0].success_rate), '0[.00]');
                this.successRate = values[0].success_rate;

                this.downTimeList = values[1]
                    .map((currentUptime: any) => {
                        currentUptime.start_at = new Date(currentUptime.start_at);
                        currentUptime.end_at = new Date(currentUptime.end_at);
                        return currentUptime;
                    })
                    .sort((a: any, b: any) => {
                        return a.start_at > b.start_at ? -1 : a.start_at < b.start_at ? 1 : 0;
                    });
                this.lastDowntime = this.downTimeList.length > 0 ? this.downTimeList[0] : null;
            })
            .finally(() => {
                this.setupDoughnutChart();
                this.loading = false;
            });
    }

    private setupDoughnutChart (): void {
        this.doughnutData = [this.successRate, 100 - this.successRate];
        this.doughnutOptions = {
            centerText: {
                percentageLabel: this.setPercentage(this.successRate),
                totalLabel: this.translateService.getString('Uptime last 30 days'),
                totalFontSize: 150,
                subFontSize: 150,
            },
            cutoutPercentage: 80,
            tooltips: {
                enabled: false,
            },
            hover: {
                mode: null,
            },
        };
    }

    private setPercentage (percentage: number): string {
        // To prevent the doughnut from showing 100% when it's not actually 100%
        let newPercentage = safeClone(percentage);
        if (newPercentage > 99) {
            newPercentage = Math.floor(newPercentage * 100) / 100;
        } else if (percentage < 1) {
            newPercentage = Math.ceil(newPercentage * 100) / 100;
        }
        return this.pipeService.get('monNumber')(newPercentage / 100, '0.[00] %');
    }
}
